import { useEffect, useMemo } from "react";
import FilterProps from "./FilterProps";
import ValidatedInput from "../../UI/ValidatedInput";
import ValidatedSelect from "../../UI/ValidatedSelect";
import { dataTypeOperatorMap } from "../../../constants/customFilterDataType";
import * as filterOperators from "../../../constants/filterOperator";
import { groupingNameMap } from "../../../constants/filterGrouping";

import IconButton from "../../UI/IconButton";
import Toggle from "../../UI/Toggle";
import { AND, OR } from "../../../constants/customFilterType";
import Checkbox from "../../UI/Checkbox";
import DateValidateResponse, { defaultValue } from "../../../utils/validation/reportFilters/DateValidateResponse";
import ErrorMessages from "../../UI/ErrorMessages";

const DateFilter = (props: FilterProps) => {
    const validation = props.validation as DateValidateResponse || defaultValue();

    const getParsedValue = (): { from: string, to: string, value: string } => {
        const defaultValues = { from: '', to: '', value: '' };
        
        if (!props.value) return defaultValues;

        if (props.value.trim().startsWith('{')) {
            try {
                const valueJson = JSON.parse(props.value);
                return {
                    from: valueJson.from || '',
                    to: valueJson.to || '',
                    value: valueJson.value || ''
                };
            } catch (e) {
                console.error("DateFilter: Failed to parse JSON", e);
                return defaultValues;
            }
        }

        return { ...defaultValues, value: props.value };
    };

    useEffect(() => {
        if (!props.value && props.onValueChange) {
            props.onValueChange(props.id, JSON.stringify(getParsedValue()));
        }
    }, [props.value]);

    useEffect(() => {
        if (props.showEndConjunction) {
            handleOnEndConjunctionChange(props.endConjunction);
        } else {
            handleOnEndConjunctionChange(undefined);
        }
    }, [props.showEndConjunction, props.endConjunction]);

    useEffect(() => {
        if (props.showStartConjunction) {
            handleOnStartConjunctionChange(props.startConjunction);
        } else {
            handleOnStartConjunctionChange(undefined);
        }
    }, [props.showStartConjunction, props.startConjunction]);

    const handleOnStartConjunctionChange = (conjunction?: string) => {
        props.onStartConjunctionChange(props.id, conjunction);
    };

    const handleOnEndConjunctionChange = (conjunction?: string) => {
        props.onEndConjunctionChange(props.id, conjunction);
    };

    const parsedValue = getParsedValue();

    const handleOnValueChange = (value: string, type: 'from' | 'to' | 'value') => {
        const newValue = { ...parsedValue, [type]: value };
        
        let valueToSend: string;
        if (props.operator === filterOperators.BETWEEN) {
            valueToSend = JSON.stringify(newValue);
        } else {
            valueToSend = newValue.value;
        }

        props.onValueChange!(props.id, valueToSend);
    };

    const getInputs = () => {
        if (!props.operator) {
            return null;
        }

        switch (props.operator) {
            case filterOperators.EQUALS:
            case filterOperators.NOT_EQUALS:
            case filterOperators.GREATER_THAN:
            case filterOperators.GREATER_THAN_OR_EQUAL_TO:
            case filterOperators.LESS_THAN:
            case filterOperators.LESS_THEN_OR_EQUAL_TO:
                return <>
                    <ValidatedInput id={`filter-date-equals-${props.id}`}
                        messages={validation.value}
                        value={parsedValue.value}
                        label="Value"
                        type="date"
                        onChange={v => handleOnValueChange(v, 'value')} />
                </>;
            case filterOperators.BETWEEN:
                return <>
                    <ErrorMessages messages={validation.fromAndTo} />
                    <div className="row align-items-center justify-content-between">
                        <div className="column-auto">
                            <ValidatedInput id={`date-filter-from-${props.id}`}
                                messages={validation.from}
                                value={parsedValue.from}
                                label="From"
                                onChange={v => handleOnValueChange(v, 'from')}
                                type="date" />
                        </div>
                        <div className="column-auto margin-bottom-5">
                            and
                        </div>
                        <div className="column-auto">
                            <ValidatedInput id={`date-filter-to-${props.id}`}
                                messages={validation.to}
                                label="To"
                                value={parsedValue.to}
                                onChange={v => handleOnValueChange(v, 'to')}
                                type="date" />
                        </div>
                    </div>
                </>
            case filterOperators.UNANSWERED:
                return null; // No input needed for UNANSWERED
            default:
                console.warn('Operator is unknown for date filter', props.operator);
                break;
        }

        return null;
    };

    const handleOnOperatorChange = (operator: string) => {
        props.onOperatorChange!(props.id, operator);

        let valueToReset: string;
        if (operator !== filterOperators.BETWEEN) {
            valueToReset = parsedValue.value;
        } else {
            valueToReset = JSON.stringify(parsedValue);
        }

        props.onValueChange!(props.id, valueToReset);
    };

    const handleOnDelete = () => {
        props.onDelete(props.id);
    };

    const handleOnSelectedChanged = (selected: boolean) => {
        props.onSelectedChange!(props.id, selected);
    };

    return <>
        {props.showStartConjunction && <Toggle id={`conjunction-${props.id}`}
            option1={{ label: 'And', value: AND }}
            option2={{ label: 'Or', value: OR }}
            onChange={handleOnStartConjunctionChange}
            value={props.startConjunction} />}
        <ErrorMessages messages={validation.default} />
        <div className="row align-items-center justify-content-between">
            <div className="column-auto">
                <Checkbox id={`filter-selected-${props.id}`}
                    checked={props.selected}
                    label="Select to Group"
                    containerClassName="unboxed margin-top-0"
                    onChange={handleOnSelectedChanged} />
            </div>
            <div className="column-auto">
                <IconButton id={`delete-filter-${props.id}`}
                    icon={<i className="ri-delete-bin-line"></i>}
                    onClick={handleOnDelete}
                    className="small flat" />
            </div>
        </div>
        <div className="row padding-3">
            <div className="column-auto text-bold">
                {groupingNameMap[props.grouping]}
            </div>
        </div>
        <div className="row align-items-center padding-3 filter-config-row">
            <div className="column-3 margin-bottom-5 filter-label-column">
                {props.name}
            </div>
            <div className="column-3 filter-operator-column">
                <ValidatedSelect id={`filter-operation-${props.id}`}
                    value={props.operator}
                    onChange={handleOnOperatorChange}
                    label="Operator"
                    messages={validation.operator}>
                    <option value="">Select One</option>
                    {dataTypeOperatorMap[props.dataType!].map((m, i) => {
                        return <option key={i} value={m}>{filterOperators.operatorLabelMap[m]}</option>
                    })}
                </ValidatedSelect>
            </div>
            <div className="column-auto filter-value-column">
                {getInputs()}
            </div>
        </div>
        {props.showEndConjunction && <Toggle id={`conjunction-${props.id}`}
            option1={{ label: 'And', value: AND }}
            option2={{ label: 'Or', value: OR }}
            onChange={handleOnEndConjunctionChange}
            value={props.endConjunction} />}
    </>
};

export default DateFilter;
